import { useState } from "react";
import type {
  FollowShipmentPayload,
  InsufficientCreditsError,
  PendingType,
  ReferenceType,
  Shipment,
  ShipmentType,
} from "api";
import { PENDING_TYPE, shipmentsApi, trackingApi } from "api";
import cn from "classnames";
import { AllSeaCarriersIcon } from "components/ui/Icons";
import { Modal } from "components/ui/Modal";
import type { SelectOption } from "components/ui/Select";
import { Select } from "components/ui/Select";
import { useCarriers } from "hooks/useCarriers";
import { getCreditDenial, isUnauthenticated } from "utils/error.utils";
import {
  isEmptyAirResult,
  isEmptyResult,
  isLimitReached,
  searchFailureReason,
  toAirFollowPayload,
  toFollowPayload,
} from "./tracking.model";

type AddShipmentModalProps = {
  onClose: () => void;
  onAdded: (shipment: Shipment) => void;
  /** Opens the "choose a plan" modal instead of reporting a failure. */
  onLimitReached: () => void;
  /** A follow the balance cannot pay for: the caller offers to buy QCredits. */
  onOutOfCredits: (denial: InsufficientCreditsError) => void;
  onSessionLost: () => void;
};

type Mode = "ocean" | "air";

/** A tab is either the kind of number to search by, or "no number yet". */
type TabKey = ReferenceType | PendingType;

type Tab = { key: TabKey; label: string; noun: string; placeholder: string };

/** The reference types a sea shipment can be looked up by. */
const OCEAN_TABS: Tab[] = [
  { key: "CONTAINER", label: "Container #", noun: "container", placeholder: "e.g. MRKU5004867" },
  { key: "BL", label: "Bill of Lading", noun: "bill of lading", placeholder: "e.g. MAEU704221885" },
  { key: "BOOKING", label: "Booking", noun: "booking", placeholder: "e.g. QT-BK-448210" },
];

const AWB_TAB: Tab = {
  key: "AWB",
  label: "Air Waybill",
  noun: "air waybill",
  placeholder: "e.g. 020-77219845",
};

/**
 * Booking not confirmed yet, but the shipment is already worth having on the
 * board. The card is saved with a route instead of a number and nothing is
 * looked up, so it costs no tracking call.
 */
const PENDING_TAB: Tab = {
  key: PENDING_TYPE,
  label: "No numbers yet",
  noun: "route",
  placeholder: "",
};

const AUTO_DETECT = "";

const AUTO_DETECT_OPTION: SelectOption = {
  value: AUTO_DETECT,
  label: "Detect automatically",
  icon: <AllSeaCarriersIcon className="pcline pcline--any" />,
};

export const AddShipmentModal = ({
  onClose,
  onAdded,
  onLimitReached,
  onOutOfCredits,
  onSessionLost,
}: AddShipmentModalProps) => {
  const [mode, setMode] = useState<Mode>("ocean");
  const [referenceType, setReferenceType] = useState<TabKey>("CONTAINER");
  const [number, setNumber] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [carrier, setCarrier] = useState(AUTO_DETECT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Null while nothing has been searched; otherwise why it turned up empty. */
  const [notFound, setNotFound] = useState<string | null>(null);

  const pending = referenceType === PENDING_TYPE;
  const carriers = useCarriers(mode === "ocean" && !pending);
  const tabs = mode === "air" ? [AWB_TAB, PENDING_TAB] : [...OCEAN_TABS, PENDING_TAB];
  const tab = tabs.find((item) => item.key === referenceType) ?? tabs[0];

  const switchMode = (next: Mode) => {
    setMode(next);
    // The pending tab means the same thing in both modes, so switching mode
    // keeps it rather than throwing the route away.
    setReferenceType((current) => (current === PENDING_TYPE ? current : next === "air" ? "AWB" : "CONTAINER"));
    setCarrier(AUTO_DETECT);
    setError(null);
    setNotFound(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    setNotFound(null);

    try {
      const { data } = await shipmentsApi.addPending({
        shipment_type: shipmentTypeOf(mode),
        origin: origin.trim() || null,
        destination: destination.trim() || null,
      });

      onAdded(data);
      onClose();
    } catch (caught: unknown) {
      if (isUnauthenticated(caught)) {
        onSessionLost();
        return;
      }

      setError("Could not add this shipment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const search = async () => {
    if (pending) {
      void submit();
      return;
    }

    const query = number.trim().toUpperCase();

    if (query === "") {
      setError(`Enter ${tab.noun === "air waybill" ? "an" : "a"} ${tab.noun} number.`);
      return;
    }

    setBusy(true);
    setError(null);
    setNotFound(null);

    try {
      const result = mode === "air" ? await followAir(query) : await followOcean(query, referenceType, carrier);

      if (result.outcome === "limit") {
        onLimitReached();
        return;
      }

      if (result.outcome === "empty") {
        setNotFound(result.reason ?? "Check the number, or pick the carrier explicitly and search again.");
        return;
      }

      const { data } = await shipmentsApi.follow(result.payload);

      onAdded(data);
      onClose();
    } catch (caught: unknown) {
      if (isUnauthenticated(caught)) {
        onSessionLost();
        return;
      }

      // Following a new shipment costs QCredits; a 402 is an offer to buy, not a failure.
      const denial = getCreditDenial(caught);
      if (denial !== null) {
        onOutOfCredits(denial);
        return;
      }

      setError("Could not add this shipment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Add shipment"
      subtitle="Sea or air — by container, B/L or booking number, or with no number at all."
      wide
      onClose={onClose}
      headerActions={
        <button type="button" className="pcbtn" onClick={onClose}>
          Close
        </button>
      }
    >
      {(box) => (
        <div className="pcnew">
          <div className="pcsh__t pcnew__modes" role="tablist" aria-label="Shipment mode">
            {(["ocean", "air"] as Mode[]).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={mode === key}
                className={cn("pcsh__tb", { "is-on": mode === key })}
                onClick={() => switchMode(key)}
              >
                {key === "ocean" ? "Sea" : "Air"}
              </button>
            ))}
          </div>

          <div className="pcsh__t pcnew__qt" role="tablist" aria-label="Reference type">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={referenceType === item.key}
                className={cn("pcsh__tb", { "is-on": referenceType === item.key })}
                onClick={() => {
                  setReferenceType(item.key);
                  setNotFound(null);
                  setError(null);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {pending ? (
            <div className="pcnew__none">
              <p>
                Tell us where you’re shipping from and to, and we’ll find the best shipping lines with the nearest
                departures, compare transit times, and show rates from multiple carriers with no commission.
              </p>
              <div className="pcnew__route">
                <label className="pcf">
                  <span>Origin</span>
                  <input
                    type="text"
                    value={origin}
                    placeholder="e.g. Shanghai CN"
                    autoComplete="off"
                    onChange={(event) => setOrigin(event.target.value)}
                  />
                </label>
                <label className="pcf">
                  <span>Destination</span>
                  <input
                    type="text"
                    value={destination}
                    placeholder="e.g. Rotterdam NL"
                    autoComplete="off"
                    onChange={(event) => setDestination(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void submit();
                    }}
                  />
                </label>
              </div>
            </div>
          ) : (
            <div className="pcnew__grid">
              <label className="pcf">
                <span>{tab.label}</span>
                <input
                  type="text"
                  value={number}
                  placeholder={tab.placeholder}
                  autoComplete="off"
                  onChange={(event) => setNumber(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void search();
                  }}
                />
              </label>

              {/* Air waybills name their airline in the number's own prefix. */}
              {mode === "ocean" && (
                <div className="pcf">
                  <span>Shipping line</span>
                  <Select
                    value={carrier}
                    options={[AUTO_DETECT_OPTION, ...carriers.options]}
                    onChange={setCarrier}
                    ariaLabel="Shipping line"
                    placeholder="Detect automatically"
                    boundsRef={box}
                  />
                </div>
              )}
            </div>
          )}

          {notFound && (
            <div className="pcnew__ask">
              <p>
                Nothing found for <b className="pcnew__q">{number.trim().toUpperCase()}</b>. Check the number, or pick
                the carrier explicitly and search again.
              </p>
            </div>
          )}

          {error !== null && <p className="pcfind__err">{error}</p>}

          <div className="pcnew__findc">
            <button
              type="button"
              className={cn("pcbtn pcbtn--main pcnew__findb", { "is-busy": busy })}
              disabled={busy}
              onClick={() => void search()}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                {pending ? (
                  <path d="M12 5v14M5 12h14" />
                ) : (
                  <>
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-4.2-4.2" />
                  </>
                )}
              </svg>
              <span className="pcnew__findb-t">Add to my Shipments</span>
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const shipmentTypeOf = (mode: Mode): ShipmentType => (mode === "air" ? "AIR" : "SEA");

/**
 * A search either produces the body of a follow, comes back empty with the
 * gateway's reason, or says the plan is spent — which the caller turns into
 * the upgrade modal rather than an error.
 */
type SearchResult =
  | { outcome: "found"; payload: FollowShipmentPayload }
  | { outcome: "empty"; reason: string | null }
  | { outcome: "limit" };

const followOcean = async (query: string, type: ReferenceType, scac: string): Promise<SearchResult> => {
  const { data } = await trackingApi.search({
    reference_number: query,
    reference_type: type,
    carrier_code: scac === AUTO_DETECT ? null : scac,
    // The gateway sends the container's last known position with this on, and
    // the saved card keeps it as its pin.
    route_details: true,
  });

  if (isLimitReached(data)) return { outcome: "limit" };
  if (isEmptyResult(data)) return { outcome: "empty", reason: searchFailureReason(data) };

  const payload = toFollowPayload(data, "SEA");

  return payload === null ? { outcome: "empty", reason: null } : { outcome: "found", payload };
};

const followAir = async (query: string): Promise<SearchResult> => {
  const { data } = await trackingApi.searchAir({ awb_number: query });

  if (isEmptyAirResult(data)) return { outcome: "empty", reason: searchFailureReason(data) };

  const payload = toAirFollowPayload(data);

  return payload === null ? { outcome: "empty", reason: null } : { outcome: "found", payload };
};
